import React from 'react'
import { CTooltip } from '@coreui/react-pro'
import PropTypes from 'prop-types'
import './classic-tooltip.style.scss'

/**
 * ClassicToolTip — A reusable tooltip wrapper around CoreUI's CTooltip.
 *
 * Renders a dark-themed tooltip (#4a4a4a background, white text) that appears
 * on hover. Gives fine-grained control over tooltip side (`placement`) and
 * arrow alignment along that side (`tipPlacement`).
 *
 * @component
 *
 * @prop {string|ReactNode} content
 *   The text or JSX rendered inside the tooltip bubble.
 *   When falsy the tooltip is skipped and only the `child` is rendered.
 *
 * @prop {'top'|'bottom'|'left'|'right'} [placement='top']
 *   Which side of the child the tooltip appears on.
 *
 * @prop {'start'|'center'|'end'} [tipPlacement='center']
 *   Arrow alignment along the tooltip edge.
 *   - 'start'  — arrow near the leading edge
 *   - 'center' — arrow centred (default)
 *   - 'end'    — arrow near the trailing edge
 *
 *   Controls both: where the tooltip body aligns relative to the trigger
 *   (via Popper.js compound placement) and where the arrow sits within
 *   the tooltip (via CSS).
 *
 * Children passed via JSX nesting act as the trigger element. Must be a
 * single DOM-backed element (or a component that forwards its ref) so
 * CoreUI can position the tooltip correctly.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * EXAMPLES
 * ────────────────────────────────────────────────────────────────────────────
 *
 * 1. Basic string tooltip (centred arrow on top):
 *
 *    <ClassicToolTip
 *      content="Save your changes"
 *      placement="top"
 *      tipPlacement="center"
 *    >
 *      <button>Save</button>
 *    </ClassicToolTip>
 *
 * 2. Arrow aligned to the start, tooltip on the bottom:
 *
 *    <ClassicToolTip
 *      content="Click to download the report"
 *      placement="bottom"
 *      tipPlacement="start"
 *    >
 *      <CButton color="primary">Download</CButton>
 *    </ClassicToolTip>
 *
 * 3. Rich JSX content (e.g. a legend list):
 *
 *    <ClassicToolTip
 *      content={
 *        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
 *          <li>40% Raw Material</li>
 *          <li>20% Packaging</li>
 *          <li>20% Labor</li>
 *        </ul>
 *      }
 *      placement="right"
 *      tipPlacement="center"
 *    >
 *      <span className="info-icon">i</span>
 *    </ClassicToolTip>
 *
 * 4. Tooltip on the left with arrow at the end:
 *
 *    <ClassicToolTip
 *      content="All pinned models for this instance will appear here"
 *      placement="left"
 *      tipPlacement="end"
 *    >
 *      <CIcon icon={cilInfo} />
 *    </ClassicToolTip>
 *
 * 5. No content — tooltip is skipped, child renders normally:
 *
 *    <ClassicToolTip content={null}>
 *      <span>No tooltip here</span>
 *    </ClassicToolTip>
 */
const ClassicToolTip = ({
  content,
  placement = 'top',
  tipPlacement = 'center',
  children,
  isOpen = undefined,
  className = '',
  style = {},
}) => {
  if (!content) return children || null

  const popperPlacement =
    tipPlacement === 'center' || !tipPlacement ? placement : `${placement}-${tipPlacement}`

  return (
    <CTooltip
      content={content}
      placement={popperPlacement}
      offset={[0, 8]}
      className={`classic-tooltip classic-tip-${tipPlacement}`}
      trigger={isOpen !== undefined ? undefined : 'hover'}
      visible={isOpen}
    >
      <div
        className={`classic-tooltip-ref ${className} text-truncate`}
        style={style}
      >
        {children}
      </div>
    </CTooltip>
  )
}

ClassicToolTip.propTypes = {
  /** Tooltip content — string or JSX. Falsy value disables the tooltip. */
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Side of the child where the tooltip appears. */
  placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  /** Arrow alignment along the tooltip edge. */
  tipPlacement: PropTypes.oneOf(['start', 'center', 'end']),
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
}

export default ClassicToolTip
